/**
 * Account Coupon Strategy
 * Handles ACCOUNT type coupons with account-level discounts
 */
import { BaseCouponStrategy } from './base-coupon-strategy';
import { couponTypes } from '../helper/constant';
import { getDefaultCouponState } from '../helper/utils';

export class AccountCouponStrategy extends BaseCouponStrategy {
  /**
   * Get coupon type identifier
   * @returns {string} ACCOUNT coupon type
   */
  getType() {
    return couponTypes.ACCOUNT;
  }

  /**
   * Calculate discount for ACCOUNT coupon
   * Account coupons apply a flat percentage to the total
   * @param {Object} couponState - Current coupon state
   * @param {Array} selectedPlans - Selected plans array (not used for account coupon)
   * @param {Object} planWithAmountMap - Plan amount mapping (not used for account coupon)
   * @param {Object} context - Additional context
   * @returns {Object} Calculated discount result
   */
  // eslint-disable-next-line no-unused-vars
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, context) {
    const accountDiscount = couponState?.account_discount || 0;

    // Account discount is a simple percentage applied to total
    // The actual calculation happens at checkout level
    return {
      ...this.getInitialResult(),
      totalDiscount: Math.abs(Number(accountDiscount)),
      isAccountLevel: true,
    };
  }

  /**
   * Convert verify coupon API result to account coupon state
   * @param {Object} apiResult - API response with discount percentage
   * @param {Object} options - Conversion options
   * @returns {Object} Coupon state
   */
  convertApiResultToState(apiResult, options = {}) {
    const { couponCode = '', allowChange = true } = options;
    const defaultState = getDefaultCouponState();

    // Account coupon has a discount percentage
    if (apiResult?.discount) {
      return {
        ...defaultState,
        account_discount: apiResult.discount * 1,
        couponType: couponTypes.ACCOUNT,
        couponCode,
        allowChange,
      };
    }

    return defaultState;
  }

  /**
   * Validate account coupon code
   * @param {string} couponCode - Coupon code to validate
   * @param {Object} context - Validation context
   * @returns {Promise<Object>} Validation result
   */
  // eslint-disable-next-line no-unused-vars
  async validate(couponCode, context) {
    return {
      valid: true,
      couponCode,
    };
  }

  /**
   * Check if account coupon can be applied
   * Account coupons are typically pre-applied at account level
   * @param {Object} context - Application context
   * @returns {boolean} Whether coupon can be applied
   */
  // eslint-disable-next-line no-unused-vars
  canApply(context) {
    return true;
  }

  /**
   * Get discount percentage for display
   * @param {Object} couponState - Coupon state
   * @returns {number} Discount percentage
   */
  getDiscountPercentage(couponState) {
    return couponState?.account_discount || 0;
  }
}

// Export singleton instance
export const accountCouponStrategy = new AccountCouponStrategy();
